import Backbone from 'backbone';
import * as d3 from 'd3';
import $ from 'jquery';
import React from 'react';
import _ from 'underscore';

import * as analytics from 'analytics/Analytics';
import BenchlingConfig from 'BenchlingConfig';
import {ResidueType} from 'biojs/BioConstants';
// @ts-expect-error
import ColorPicker from 'components/ColorPicker';
import {Sequence} from 'components/Sequence';
import {ANALYTICS_MODULE, ACTION_CATEGORY} from 'constants/AnalyticsConstants';
import * as colors from 'lib/Colors';
import Annotation from 'molbio/annotations/backbone/Annotation';
import AddNewFeatureForm from 'molbio/feature-libraries/AddNewFeatureForm';
import {EditableTableView} from 'molbio/feature-libraries/Editables';
import EditableTableItemView from 'molbio/feature-libraries/EditableTableItemView';
import {Feature as ComponentFeature, Model as FeatureLibrary} from 'molbio/feature-libraries/FeatureLibrary';
import {
  deleteFeaturesFromLibrary,
  moveFeaturesToLibrary,
  updateFeatures,
} from 'molbio/feature-libraries/FeatureLibraryActions';
import {Feature} from 'molbio/feature-libraries/FeatureLibraryConstants';
import FeatureTable from 'molbio/feature-libraries/FeatureTable';
import LibraryItemTemplate from 'molbio/feature-libraries/LibraryItemTemplate';
import {Collection as Selectables} from 'molbio/maps/selectables/Selectables';
import * as sequtil from 'molbio/SeqUtil';
import notifications from 'notifications';
import {renderToDOM} from 'redux-stores/ReduxDOMRender';
import {BasicEvent} from 'util/Types';

class LibraryActionMenu extends Backbone.View<Backbone.Model> {
  members!: Selectables<ComponentFeature> | null;
  library!: FeatureLibrary;

  static initClass(): void {
    this.prototype.events = {
      // @ts-expect-error: Backbone events can be either the events hash or a method returning the events hash
      // The Backbone types do not know about this.
      'change .select-all-checkbox input': 'toggleSelected',
      'click .move-options a': 'doMove',
      'click .library-actions .delete-button': 'doDelete',
    };
  }

  initialize(options: unknown): void {}

  die(): void {
    this.stopListening();
    this.undelegateEvents();
  }

  toggleSelected(e: BasicEvent): void {
    const checked = $(e.target).prop('checked');
    if (checked) {
      this.members!.each((model) => {
        model.select({multi: true});
      });
    } else {
      this.members!.deselect();
    }
    this.updateActions();
  }

  doMove(e: BasicEvent): void {
    let member;
    const destinationLibraryId = $(e.target).data('libraryId');
    const destinationLibrary = this.library.collection.get(destinationLibraryId);
    const members = this.members!.where({selected: true});
    if (members.length === 0) {
      return;
    }
    this.members!.deselect();
    this.members!.remove(members);

    destinationLibrary.get('features').add(members);
    moveFeaturesToLibrary(
      Array.from(members).map((m) => m.id),
      this.library.id,
      destinationLibraryId
    )
      .fail(notifications.notifyError)
      .done(() => {
        this.updateActions();
        for (member of Array.from(members)) {
          member.set({library_id: destinationLibraryId});
          analytics.track('feature_library', 'moved_feature', {
            product: ANALYTICS_MODULE.MOLBIO,
            actionCategory: ACTION_CATEGORY.WRITE,
            sourceFeatureLibraryId: this.library.get('api_identifier'),
            destinationFeatureLibraryId: destinationLibrary.get('api_identifier'),
            featureId: member.id,
          });
        }
        notifications.success(
          <>
            Successfully moved {members.length} items to <strong>{destinationLibrary.get('name')}</strong>.
          </>
        );
      });
  }

  doDelete(): void {
    const members = this.members!.where({selected: true});
    if (members.length === 0) {
      return;
    }
    this.members!.remove(members);
    this.updateActions();
    deleteFeaturesFromLibrary(
      Array.from(members).map((member) => member.id),
      this.library.get('id')
    )
      .fail(notifications.notifyError)
      .done(() => {
        notifications.success(`Successfully deleted ${members.length} item(s).`);
        members.forEach((member) => {
          analytics.track('feature_library', 'deleted_feature', {
            product: ANALYTICS_MODULE.MOLBIO,
            actionCategory: ACTION_CATEGORY.WRITE,
            featureLibraryId: this.library.get('api_identifier'),
            featureId: member.id,
          });
        });
      });
  }

  updateActions(): void {
    const $checkboxes = this.$el.find('.library-item input[type=checkbox]');
    const total = $checkboxes.length;
    const selected = this.members!.where({selected: true}).length;
    const $selectAll = this.$el.find('.select-all-checkbox input');
    $selectAll.prop({
      checked: selected > 0,
      indeterminate: selected > 0 && selected < total,
    });
    if (selected > 0) {
      this.$el.find('.actions .btn').removeClass('disabled');
    } else {
      this.$el.find('.actions .btn').addClass('disabled');
    }
  }

  update(library: FeatureLibrary): void {
    this.library = library;
    if (this.members != null) {
      this.stopListening(this.members);
    }
    this.members = this.library.get('features');

    this.listenTo(this.members, 'change:selected', this.updateActions.bind(this));
    this.render();
  }

  render(): LibraryActionMenu {
    let library;
    if (!this.library.get('permissions').writable) {
      this.$el.find('.library-actions').addClass('hidden');
      this.$el.find('.library-list').addClass('non-writable');
      return this;
    }

    this.$el.find('.library-actions').removeClass('hidden');
    this.$el.find('.library-list').removeClass('non-writable');
    const $select = $('.actions .move-options');
    const $els = [];
    for (library of this.library.collection.models) {
      if (!library.get('permissions').writable || library.id === this.library.id) {
        continue;
      }
      const $li = $('<li>');
      $els.push(
        $li.append(
          $('<a>')
            .attr('data-test-component', 'FeatureLibraryActionMenu')
            .attr('data-test-element', 'targetLibraryOption')
            .attr('data-test-key', library.get('name'))
            .text(library.get('name'))
            .data('libraryId', library.id)
        )
      );
    }
    if ($els.length > 0) {
      // @ts-expect-error: Need to figure out the JQuery types here
      $select.html($els);
    } else {
      const $disabled = $('<li class="disabled">');
      $disabled.append($('<a>').text('No possible libraries'));
      // @ts-expect-error: Need to figure out the JQuery types here
      $select.html($disabled);
    }
    this.$el.find('.select-all-checkbox input').prop({
      checked: false,
      indeterminate: false,
    });
    this.$el.find('.library-item input[type=checkbox]').prop('checked', false);
    this.$el.find('.actions .btn').addClass('disabled');

    return this;
  }
}
LibraryActionMenu.initClass();

interface FeatureTableViewOptions extends Backbone.ViewOptions<ComponentFeature> {
  library: FeatureLibrary;
  sequence: Sequence;
}

class FeatureTableView extends EditableTableView<ComponentFeature> {
  editableFields!: Array<string>;
  library!: FeatureLibrary;
  collection!: Selectables<ComponentFeature>;
  comparators: unknown;
  comparator!: string;
  reverse!: boolean;
  models!: Array<unknown>;
  paginatedModels!: Array<unknown>;

  static initClass(): void {
    this.prototype.editableFields = ['name', 'feature_type', 'bases', 'color'];
  }

  initialize(options: FeatureTableViewOptions): void {
    this.library = options.library;
    this.collection = this.library.get('features');
    this.comparators = {
      type(annotation: Annotation) {
        return annotation.annotationType().toLowerCase();
      },
      name(annotation: Annotation) {
        return annotation.name().toLowerCase();
      },
      start(annotation: Annotation) {
        return annotation.start();
      },
      end(annotation: Annotation) {
        return annotation.end();
      },
      strand(annotation: Annotation) {
        return annotation.strand();
      },
      length(annotation: Annotation) {
        return sequtil.length(annotation.start(), annotation.end(), options.sequence.length());
      },
    };
    this.comparator = 'name';
    this.reverse = false;
    super.initialize();
  }

  allowEdits(): boolean {
    return this.library.get('permissions').writable;
  }

  allowSelecting(): boolean {
    return this.allowEdits();
  }

  _handleUpdateHelper(type: keyof Feature, value: Feature[keyof Feature]): void {
    let member;
    const count = this.collection.where({selected: true}).length;
    const save: Partial<Feature> = {};
    // @ts-expect-error: Typescript has no way to tell that the values are being set to the correct keys
    save[type] = value;
    for (const feature of this.collection.where({selected: true})) {
      const invalid = feature.validate(Object.assign(feature.toJSON(), save));
      if (invalid) {
        this.update();
        notifications.error(invalid);
        return;
      }
    }
    const members = this.collection.where({selected: true});

    // Some users have made it to here without any features selected. Finding the source
    // of the error was hard, but we only need to make a request if any items were selected anyway.
    if (members.length > 0) {
      const data: Array<Partial<Feature>> = [];
      for (member of Array.from(members)) {
        data.push({id: member.id});
      }
      for (const d of data) {
        // @ts-expect-error: Typescript has no way to tell that the values are being set to the correct keys
        d[type] = value;
      }

      for (const member of Array.from(members)) {
        member.set(type, value);
      }

      updateFeatures(data, this.library.get('id'))
        .fail((res) => {
          this.batchFetch();
          notifications.notifyError(res);
        })
        .done(() => {
          notifications.success(`${count} feature${count !== 1 ? 's were' : ' was'} saved \
successfully.`);
          members.forEach((m) => {
            analytics.track('feature_library', 'edited_feature', {
              product: ANALYTICS_MODULE.MOLBIO,
              actionCategory: ACTION_CATEGORY.WRITE,
              featureLibraryId: this.library.get('api_identifier'),
              featureId: m.id,
            });
          });
        });
    }

    this.render();
  }

  reset(library: FeatureLibrary): void {
    this.library = library;
    this.collection = library.get('features');
    this.stopListening();
    this.registerListeners();
    this.render();
  }

  batchFetch(): void {
    // @ts-expect-error: Potentially the merge option does nothing, needs investigation
    this.library.fetch({merge: true}).done(this.update.bind(this));
  }

  makeRow(feature: ComponentFeature): FeatureItemView {
    return new FeatureItemView({model: feature});
  }
  getRows(): Array<ComponentFeature> {
    // models = _.sortBy(@collection.models, @comparators[@comparator])
    let {models} = this.collection;
    if (this.reverse) {
      models = models.reverse();
    }
    this.models.splice(0, this.models.length, ...models);
    this.paginatedModels.splice(0, this.paginatedModels.length, ...models);
    const rows = models.slice();
    return rows;
  }
}
FeatureTableView.initClass();

class FeatureItemView extends EditableTableItemView {
  template!: typeof LibraryItemTemplate;
  tagName!: string;
  className!: string;
  colorPicker: typeof ColorPicker;
  model!: ComponentFeature;

  static initClass(): void {
    this.prototype.template = LibraryItemTemplate;
    this.prototype.tagName = 'tr';
    this.prototype.className = 'library-item';
  }

  events(): Backbone.EventsHash {
    return Object.assign(super.events(), {'click .checkbox-active': 'updateActiveFromCheckbox'});
  }

  deleteItem(e: BasicEvent): void {
    e.stopPropagation();
    return this.model
      .destroy()
      .done(() => {
        notifications.success('Item deleted successfully.');
      })
      .fail(notifications.notifyError);
  }

  initialize(options: unknown): void {
    // TODO (BNCH-47280): Replace ColorPicker with react version
    this.colorPicker = new ColorPicker();
    // @ts-expect-error Argument expected
    super.initialize();
  }

  die(): void {
    super.die();
    this.colorPicker.die();
  }

  update(): void {
    // HACK: This has to use .html(), because .text() actually changes the text node in the span. This
    // prevents any ongoing events on the elements to continue (if you change the text during a mousedown event,
    // the click event never gets fired.)
    this.$el.find('.name').html(_.escape(this.model.name()));
    this.$el.find('.bases').val(this.model.bases());
    this.$el.find('.feature_type').val(this.model.featureType());
    return this.colorPicker.update(this.model);
  }

  updateActiveFromCheckbox(e: BasicEvent): void {
    e.stopPropagation();
    if (this.$el.find('.checkbox-active').prop('checked')) {
      this.model.select({multi: true});
    } else {
      this.model.set({selected: false});
    }
  }

  updateSelected(): void {
    super.updateSelected();
    this.$el.find('.checkbox-active').prop('checked', this.model.selected());
  }

  render(): FeatureItemView {
    this.$el.html(
      // eslint-disable-next-line new-cap
      LibraryItemTemplate({
        feature: this.model.toJSON(),
      })
    );

    this.colorPicker.attach(this.$el.find('.swatch'), 'manual', 'body');
    this.colorPicker.update(this.model);

    this.updateSelected();
    super.render();
    return this;
  }
}
FeatureItemView.initClass();

export interface LibraryViewOptions extends Backbone.ViewOptions<FeatureLibrary> {
  libraryMember: typeof ComponentFeature;
  collectionName: string;
}

class LibraryView extends Backbone.View<FeatureLibrary> {
  actionMenu!: LibraryActionMenu;
  table!: FeatureTableView | null;
  library!: FeatureLibrary;
  libraryMember!: typeof ComponentFeature;
  collectionName!: string;
  comparator!: string;
  reverse!: boolean;
  members!: Backbone.Collection<ComponentFeature>;

  static initClass(): void {
    this.prototype.el = '.library-view';
    this.prototype.events = {
      // @ts-expect-error: Backbone events can be either the events hash or a method returning the events hash
      // The Backbone types do not know about this.
      'click .new-library-member .color-chooser li': 'chooseColor',
      'click th span.comparator': 'handleSort',
      'submit form.create-feature': 'createLibraryMember',
    };
  }

  initialize(options: LibraryViewOptions): void {
    this.libraryMember = options.libraryMember;
    this.collectionName = options.collectionName;
    this.comparator = 'name';
    this.reverse = false;
    this.actionMenu = new LibraryActionMenu({
      el: this.$el.find('.library-data'),
    });
  }

  die(): void {
    this.stopListening();
    this.undelegateEvents();
    this.actionMenu.die();
    if (this.table != null) {
      this.table.die();
    }
  }

  chooseColor(e: BasicEvent): void {
    const $target = $(e.target);
    $target.parent().find('.active').removeClass('active');
    $target.addClass('active');
  }

  createFeature(newFeature: Partial<Feature>): void {
    // eslint-disable-next-line new-cap
    const member = new this.libraryMember(newFeature);
    const invalid = member.validate(member.toJSON());
    if (invalid) {
      notifications.error(invalid);
      return;
    }
    member
      .save()
      .done(() => {
        this.library.get(this.collectionName).add(member);
        this.$el.find('.new-library-member input:text, .new-library-member textarea').val('');
        notifications.success('Item added to folder successfully.');
        this.$el.find('.new-library-member .color-chooser li').eq(0).addClass('active');
        analytics.track('feature_library', 'added_feature', {
          product: ANALYTICS_MODULE.MOLBIO,
          actionCategory: ACTION_CATEGORY.WRITE,
          featureLibraryId: this.library.get('api_identifier'),
          featureId: member.id,
          featureType: member.get('feature_type'),
          matchType: member.get('match_type'),
          name: member.get('name'),
        });
      })
      .fail(notifications.notifyError);
  }

  createLibraryMember(e: BasicEvent): void {
    e.preventDefault();
    const attrs: Partial<Feature> = {
      name: this.$el.find('.new-library-member .name').val(),
      bases: this.$el.find('.new-library-member .bases').val(),
      color: d3
        .rgb(this.$el.find('.new-library-member .color-chooser .active').css('background-color'))
        .toString(),
      match_type: this.$el.find('.match_type:checked').val(),
      library_id: this.library.id,
    };
    const typeNode = this.$el.find('.new-library-member .type');
    if (typeNode.length !== 0) {
      attrs.feature_type = typeNode.val();
    }
    this.createFeature(attrs);
  }

  update(library: FeatureLibrary): void {
    this.library = library;
    this.stopListening();
    this.members = this.library.get(this.collectionName);
    this.listenTo(this.members, 'add remove', this.render.bind(this));

    this.actionMenu.update(this.library);
  }

  handleSort(e: BasicEvent): void {
    const comparator = $(e.currentTarget).attr('data-name');
    if (comparator === this.comparator) {
      this.reverse = !this.reverse;
    }
    this.comparator = comparator;
    this.members.comparator = this.comparator;
    // eslint-disable-next-line no-restricted-syntax
    this.members.sort();
    this.render();
  }

  render(): LibraryView {
    this.$el.find('.library-name').text(this.library.get('name')).removeClass('hidden');
    if (this.library.get('permissions').writable) {
      this.$el.find('.new-library-member').removeClass('hidden');
    } else {
      this.$el.find('.new-library-member').addClass('hidden');
    }

    const models = _.clone(this.members.models);
    if (this.reverse) {
      models.reverse();
    }

    if (BenchlingConfig.MOLBIO_REACT_FEATURE_LIBRARY_TABLE) {
      const features = this.members.models.map((feature) => feature.attributes);
      const featureCollection = this.members;
      const allMembers = this.members.models;
      renderToDOM(
        <>
          {this.library.get('permissions').writable && (
            <AddNewFeatureForm
              onAddNewFeature={(newFeature) => {
                this.createFeature({
                  name: newFeature.name,
                  bases: newFeature.residues,
                  color: newFeature.color,
                  match_type: newFeature.residueType === ResidueType.NUCLEOTIDE ? 'nucleotide' : 'protein',
                  feature_type: newFeature.featureType,
                  library_id: this.library.id,
                });
              }}
            />
          )}
          <FeatureTable
            isReadOnly={!this.library.get('permissions').writable}
            features={features}
            libraries={this.library.collection.models.map((library) => library.attributes)}
            currentLibraryId={this.library.get('api_identifier')}
            onUpdateFeature={(featureIndex, field, value) => {
              updateFeatures([{id: features[featureIndex].id, [field]: value}], this.library.id)
                .fail((res) => {
                  notifications.notifyError(res);
                })
                .done(() => {
                  this.library.fetch().done(this.render.bind(this));
                  notifications.success(`1 feature was saved successfully.`);
                  analytics.track('feature_library', 'edited_feature', {
                    product: ANALYTICS_MODULE.MOLBIO,
                    actionCategory: ACTION_CATEGORY.WRITE,
                    featureLibraryId: this.library.get('api_identifier'),
                    featureId: features[featureIndex].id,
                  });
                });
            }}
            onMoveToLibrary={(libraryId, featureIndices) => {
              const destinationLibrary = this.library.collection.models.find(
                (m) => m.get('api_identifier') === libraryId
              );
              moveFeaturesToLibrary(
                featureIndices.map((featureIndex) => features[featureIndex]).map((feature) => feature.id),
                this.library.id,
                destinationLibrary.id
              )
                .fail(notifications.notifyError)
                .done(() => {
                  for (const featureIndex of featureIndices) {
                    allMembers[featureIndex].set({
                      library_id: destinationLibrary.id,
                      selected: false,
                    });
                    analytics.track('feature_library', 'moved_feature', {
                      product: ANALYTICS_MODULE.MOLBIO,
                      actionCategory: ACTION_CATEGORY.WRITE,
                      sourceFeatureLibraryId: this.library.get('api_identifier'),
                      destinationFeatureLibraryId: libraryId,
                      featureId: features[featureIndex].id,
                    });
                  }
                  const membersToMove = allMembers.filter((_, index) => featureIndices.includes(index));
                  featureCollection.remove(membersToMove);
                  destinationLibrary.get('features').add(membersToMove);
                  notifications.success(
                    <>
                      Successfully moved {featureIndices.length} items to{' '}
                      <strong>{destinationLibrary.get('name')}</strong>.
                    </>
                  );
                });
            }}
            onDeleteFeatures={(featureIndices) => {
              deleteFeaturesFromLibrary(
                featureIndices.map((featureIndex) => features[featureIndex].id),
                this.library.get('id')
              )
                .fail(notifications.notifyError)
                .done(() => {
                  const members = allMembers.filter((_, index) => featureIndices.includes(index));
                  featureCollection.remove(members);
                  notifications.success(`Successfully deleted ${featureIndices.length} item(s).`);
                  featureIndices.forEach((featureIndex) => {
                    analytics.track('feature_library', 'deleted_feature', {
                      product: ANALYTICS_MODULE.MOLBIO,
                      actionCategory: ACTION_CATEGORY.WRITE,
                      featureLibraryId: this.library.get('api_identifier'),
                      featureId: features[featureIndex].id,
                    });
                  });
                });
            }}
          />
        </>,
        this.el
      );
    } else {
      if (this.table) {
        this.table.reset(this.library);
      } else {
        this.table = new FeatureTableView({
          el: this.$el.find('tbody'),
          library: this.library,
          collection: this.members,
        } as FeatureTableViewOptions);
      }

      this.table.render();
    }

    const $colors = this.$el.find('.new-library-member .color-chooser');
    if ($colors.find('li').length === 0) {
      for (const color of colors.pastelColors) {
        const $li = $('<li>').css('background-color', color);
        $colors.append($li);
      }
    }

    this.$el.find('.help').tooltip({
      placement: 'top',
      container: 'body',
    });
    this.$el.find('.new-library-member .color-chooser li').eq(0).addClass('active');
    return this;
  }
}
LibraryView.initClass();

export default LibraryView;
